import React from 'react'
import { useSelector } from 'react-redux'
import { FormattedMessage, defineMessages } from 'react-intl'
import { useFormatMessage } from 'src/providers/intl'
import { OVERLAY_HELPERS } from 'src/constants/navigation'
import { EMERGENCY_RESERVE } from 'src/constants/gems'
import { FIX_MORTGAGE_TYPE, LT30 } from 'src/constants/loan'
import { selectContactFirstName } from 'src/store/selectors/contact'
import {
  selectShouldRenderRefiOptions,
  selectShouldRenderEmergencyReserve
} from 'src/store/selectors/display'
import { Text, Button, Page } from 'src/Components'
import { OverlayQuestionsFooter } from 'src/Components/shared/Routing'
import { ProConList, Pro, Info } from 'src/Components/shared/Gem/GemDetail'
import { buildKey } from 'src/Components/homeowners/RefiDetailsOverlay/RefiDetailsOverlay'
import history from 'src/history'

import styles from './MortgageRelief.module.scss'

const MortgageRelief = () => {
  const intlFormat = useFormatMessage()

  const loanOfficerFirstName = useSelector(selectContactFirstName)
  const displayRefiOptions = useSelector(selectShouldRenderRefiOptions)
  const displayEmergencyReserve = useSelector(
    selectShouldRenderEmergencyReserve
  )

  let reducedMonthlyPaymentLink = intlFormat(linkMessages.reducedMonthlyPayment)
  if (displayRefiOptions) {
    reducedMonthlyPaymentLink = renderCopyLink(
      reducedMonthlyPaymentLink,
      () => {
        const refiKey = buildKey({
          years: LT30,
          type: FIX_MORTGAGE_TYPE
        })
        history.push(OVERLAY_HELPERS.refinance.buildPath(refiKey))
      }
    )
  }

  let cashSafetyNetLink = intlFormat(linkMessages.cashSafetyNet)
  if (displayEmergencyReserve) {
    cashSafetyNetLink = renderCopyLink(cashSafetyNetLink, () => {
      history.push(OVERLAY_HELPERS.cashout.buildPath(EMERGENCY_RESERVE))
    })
  }

  return (
    <Page className={styles.container}>
      <Page.Body className={styles.body}>
        <Text size='l' align='center'>
          <FormattedMessage
            id='Homeowner.mortgageReliefOptions.header'
            defaultMessage='What could all the talk about mortgage payments mean for you?'
          />
        </Text>
        <ProConList className={styles.optionsWrapper}>
          <Info>
            <FormattedMessage
              id='Homeowner.mortgageRelief.option1'
              defaultMessage='It’s always best to keep paying if you can. Relief programs (like “forbearance”) do not mean free payments -- they typically extend your loan so you can make those payments later. Doing this may help you avoid damage to your credit score; it could also impact your ability to qualify for future financing. Be sure to weigh the pros and cons.'
            />
          </Info>
          <Pro>
            <FormattedMessage
              id='Homeowner.mortgageReliefOptions.option2'
              defaultMessage='Would a lower monthly payment help? Ask {loanOfficerFirstName} if you could qualify for a refinance to {reducedMonthlyPayment}. Or, you could ask about using some of your equity to pull out {cashSafetyNet}.'
              values={{
                loanOfficerFirstName,
                reducedMonthlyPayment: reducedMonthlyPaymentLink,
                cashSafetyNet: cashSafetyNetLink
              }}
            />
          </Pro>
          <Pro>
            <FormattedMessage
              id='Homeowner.mortgageReliefOptions.option3'
              defaultMessage='If you need it, you may be able to pause payments up to 12 months. But {bold} by your loan servicer (the company that collects your monthly payment) for forbearance.'
              values={{
                bold: (
                  <Text element='span' weight='semibold'>
                    <FormattedMessage
                      id='Homeowner.mortgageReliefOptions.option3.bold'
                      defaultMessage='you must apply for and be approved'
                    />
                  </Text>
                )
              }}
            />
            <Text weight='semibold' element='span' className={styles.highlight}>
              <FormattedMessage
                id='Homeowner.mortgageReliefOptions.option3.foot'
                defaultMessage='You can find your loan servicer on your mortgage bill or online statement.'
              />
            </Text>
          </Pro>
        </ProConList>
      </Page.Body>
      <Page.Footer showDisclaimer>
        <OverlayQuestionsFooter
          topic='Mortgage Payment Options'
          questions={Object.values(questionMessages)}
          placeholder
          disableCospoMessage
        />
      </Page.Footer>
    </Page>
  )
}

const renderCopyLink = (copy, onClick) => (
  <Button minimal element='a' onClick={onClick}>
    {copy}
  </Button>
)

const linkMessages = defineMessages({
  reducedMonthlyPayment: {
    id: 'Homeowner.mortgageReliefOptions.option2.reducedMonthlyPayment',
    defaultMessage: 'reduce your monthly payment'
  },
  cashSafetyNet: {
    id: 'Homeowner.mortgageReliefOptions.option2.cashSafetyNet',
    defaultMessage: 'cash for a safety net'
  },
  seeLoanDetails: {
    id: 'Homeowner.mortgageReliefOptions.option2.seeLoanDetails',
    defaultMessage: 'See loan details.'
  }
})

const questionMessages = defineMessages({
  whatOptions: {
    id: 'Homeowner.mortgageRelief.questions.whatOptions',
    defaultMessage: 'What options make sense for me?'
  },
  qualifyRefi: {
    id: 'Homeowner.mortgageRelief.questions.qualifyRefi',
    defaultMessage: 'Could I qualify for a refi?'
  }
})

export default MortgageRelief
